import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors.
// IMPORTANT: a 401 on the /auth/* endpoints themselves (login, register,
// google/session, password-reset) is a *credential error*, not a token-expiry.
// The page-level components already render inline error UI for those, so the
// global redirect must skip them — otherwise the user is silently bounced to
// /login with no explanation (and in the Google-callback case, AuthCallback's
// error card is clobbered before it can render).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint = /\/auth\//.test(url);
      if (!isAuthEndpoint) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  requestPasswordReset: (email) => api.post('/auth/password-reset-request', { email }),
  confirmPasswordReset: (data) => api.post('/auth/password-reset-confirm', data),
  getCurrentUser: () => api.get('/auth/me'),
  me: () => api.get('/auth/me'),
  googleSession: (session_id) => api.post('/auth/google/session', { session_id }),
};

export const stlAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/stl/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  analyze: (analysisId) => api.get(`/stl/analyze/${analysisId}`),
  getHistory: (limit = 20) => api.get('/stl/history', { params: { limit } }),
};

export const imageAPI = {
  upload: (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post('/image/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  analyze: (analysisId) => api.get(`/image/analyze/${analysisId}`),
  getHistory: (limit = 20) => api.get('/image/history', { params: { limit } }),
};

export const chatAPI = {
  sendMessage: (message, sessionId = null) =>
    api.post('/chat', { message, session_id: sessionId }),
};

export const printerAPI = {
  create: (data) => api.post('/printers', data),
  getAll: () => api.get('/printers'),
};

export const materialAPI = {
  create: (data) => api.post('/materials', data),
  getAll: () => api.get('/materials'),
};

export const slicerAPI = {
  getRecommendations: (printerModel, materialType) =>
    api.get('/slicer/recommendations', {
      params: { printer_model: printerModel, material_type: materialType },
    }),
};

export const pointsAPI = {
  getBalance: () => api.get('/points/balance'),
  getHistory: (limit = 50) => api.get('/points/history', { params: { limit } }),
  redeem: (item) => api.post('/points/redeem', { item }),
};

export const usageAPI = {
  getCurrent: () => api.get('/usage/current'),
};

export const supportAPI = {
  getRoiSummary: () => api.get('/supports/roi-summary'),
  getHistory: (limit = 20) => api.get('/supports/history', { params: { limit } }),
};

export const subscriptionAPI = {
  createCheckout: (plan) => api.post('/subscription/create-checkout', { plan, origin_url: window.location.origin }),
  getCheckoutStatus: (sessionId) => api.get(`/subscription/checkout-status/${sessionId}`),
  createPortal: () => api.post('/subscription/create-portal'),
};

export default api;
